import React, { useState, useRef, useCallback } from 'react';
import { Trash2, GripVertical, Check, ChevronDown, ChevronRight, ChevronUp, Ungroup, Lock, GitMerge, Package } from 'lucide-react';
import { formatPrice } from '../../../utils/formatPrice';

import '../../../css/models-common.css';
import '../../../css/model-builder.css';

/**
 * Table component for displaying and editing model components with sub-product support
 * Sub-products appear as distinct card blocks with nested headers
 */
const ModelBuilderTable = ({
    components,
    subProducts = [],
    onRemove,
    onUpdate,
    onUpdateInSubProduct,
    onReorder,
    onRemoveSubProduct,
    onToggleSubProductCollapse,
    onUpdateSubProduct,
    onRemoveFromSubProduct,
    onUngroupSubProduct,
    onDropItem,
    onGroupSelected,
    selectedCount = 0
}) => {
    // Internal drag-to-reorder state
    const [dragIndex, setDragIndex] = useState(null);
    const [dropTargetIndex, setDropTargetIndex] = useState(null);
    const dragRef = useRef(null);

    const handleReorderDragStart = useCallback((e, index) => {
        setDragIndex(index);
        dragRef.current = index;
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/x-reorder', String(index));
    }, []);

    const handleReorderDragOver = useCallback((e, index) => {
        e.preventDefault();
        if (dragRef.current === null) return;
        e.dataTransfer.dropEffect = 'move';
        setDropTargetIndex(index);
    }, []);

    const handleReorderDrop = useCallback((e, toIndex) => {
        e.preventDefault();
        const fromIndex = dragRef.current;
        if (fromIndex !== null && fromIndex !== toIndex && onReorder) {
            onReorder(fromIndex, toIndex);
        }
        setDragIndex(null);
        setDropTargetIndex(null);
        dragRef.current = null;
    }, [onReorder]);

    const handleReorderDragEnd = useCallback(() => {
        setDragIndex(null);
        setDropTargetIndex(null);
        dragRef.current = null;
    }, []);

    const handleToggleSelect = (id) => {
        const component = components.find(c => c.id === id);
        if (component) {
            onUpdate(id, { selected: !component.selected });
        }
    };

    // Render a single component row (for main list or inside sub-product)
    const renderComponentRow = (component, isSubProductChild = false, subProductId = null, index = 0) => {
        const componentCost = (parseFloat(component.price) || 0) * (component.amount || 1);
        const isBeingDragged = !isSubProductChild && dragIndex === index;
        const isDropTarget = !isSubProductChild && dropTargetIndex === index && dragIndex !== null && dragIndex !== index;

        // Use the correct update handler depending on context
        const handleUpdate = (id, updates) => {
            if (isSubProductChild && onUpdateInSubProduct) {
                onUpdateInSubProduct(subProductId, id, updates);
            } else {
                onUpdate(id, updates);
            }
        };

        return (
            <div
                key={component.id}
                className={`component-row ${component.selected ? 'selected' : ''} ${isSubProductChild ? 'nested' : ''} ${isBeingDragged ? 'dragging' : ''} ${isDropTarget ? 'drop-target' : ''}`}
                draggable={!isSubProductChild}
                onDragStart={!isSubProductChild ? (e) => handleReorderDragStart(e, index) : undefined}
                onDragOver={!isSubProductChild ? (e) => handleReorderDragOver(e, index) : undefined}
                onDrop={!isSubProductChild ? (e) => handleReorderDrop(e, index) : undefined}
                onDragEnd={!isSubProductChild ? handleReorderDragEnd : undefined}
            >
                <div className="col-grip">
                    {!isSubProductChild && (
                        <GripVertical size={14} className="grip-handle" />
                    )}
                </div>

                <div className="col-checkbox">
                    {!isSubProductChild && (
                        <div
                            className={`glass-checkbox ${component.selected ? 'checked' : ''}`}
                            onClick={() => handleToggleSelect(component.id)}
                        >
                            {component.selected && <Check size={12} color="#fff" />}
                        </div>
                    )}
                </div>

                <div className="col-name">
                    <input
                        type="text"
                        className="glass-pill-input"
                        value={component.name}
                        readOnly
                        title={component.name}
                    />
                </div>

                <div className="col-price">
                    <span className="price-display" title={`Base: ${formatPrice(parseFloat(component.price) || 0)} × ${component.amount || 1}`}>
                        {formatPrice(componentCost)}
                    </span>
                </div>

                <div className="col-amount">
                    <input
                        type="number"
                        className="glass-pill-input"
                        value={component.amount}
                        onChange={(e) => handleUpdate(component.id, { amount: parseInt(e.target.value) || 1 })}
                    />
                </div>

                <div className="col-currency">
                    <div className="currency-pill-wrapper">
                        <div className="currency-icon-dot">$</div>
                        <select
                            className="glass-pill-select"
                            value={component.currency}
                            onChange={(e) => handleUpdate(component.id, { currency: e.target.value })}
                        >
                            <option value="USD">USD</option>
                            <option value="EUR">EUR</option>
                            <option value="INR">INR</option>
                            <option value="CNY">CNY</option>
                        </select>
                    </div>
                </div>

                <div className="col-unit">
                    <select
                        className="glass-pill-select"
                        value={component.unit}
                        onChange={(e) => handleUpdate(component.id, { unit: e.target.value })}
                    >
                        <option value="kg">kg</option>
                        <option value="mt">mt</option>
                        <option value="lb">lb</option>
                        <option value="unit">unit</option>
                    </select>
                </div>

                <div className="col-source">
                    <input
                        type="text"
                        className="glass-pill-input"
                        value={component.source}
                        onChange={(e) => handleUpdate(component.id, { source: e.target.value })}
                    />
                </div>

                <div className="col-destination">
                    <input
                        type="text"
                        className="glass-pill-input"
                        value={component.destination}
                        onChange={(e) => handleUpdate(component.id, { destination: e.target.value })}
                    />
                </div>

                <div className="col-actions">
                    <button
                        className="btn-action-pill red"
                        onClick={() => isSubProductChild ? onRemoveFromSubProduct(subProductId, component.id) : onRemove(component.id)}
                    >
                        <Trash2 size={14} />
                    </button>
                </div>
            </div>
        );
    };

    // Render a sub-product card
    const renderSubProduct = (subProduct) => {
        const isLocked = subProduct.locked;
        

        return (
            <div key={subProduct.id} className={`subproduct-card ${isLocked ? 'locked' : ''}`}>
                <div className="model-builder-product-row sub-product-view-mode">
                    <div className="product-top-row sub-product-top-row">
                        {/* Image Placeholder */}
                        <div className="product-image-container">
                            <div className="product-image-placeholder product-image-placeholder--small">
                                <div className="sub-product-image-inner">
                                    <Package size={24} color="rgba(255,255,255,0.2)" />
                                </div>
                            </div>
                        </div>

                        {/* Name Input */}
                        <div className="form-group form-group--flex">
                            <label className="form-label-mini form-label-mini--sub">Sub-Product Name</label>
                            <input
                                type="text"
                                className="glass-input-main glass-input-main--inline"
                                value={subProduct.name}
                                onChange={(e) => !isLocked && onUpdateSubProduct(subProduct.id, { name: e.target.value })}
                                placeholder="Sub-Product Name"
                                readOnly={isLocked}
                            />
                        </div>

                        {/* Controls Grid */}
                        <div className="product-controls-grid sub-product-controls">
                            <div className="form-group form-group--w-80">
                                <label className="form-label-mini form-label-mini--sub">Price</label>
                                <input
                                    type="text"
                                    className="glass-box-input"
                                    value={formatPrice(subProduct.subtotal || 0)}
                                    readOnly
                                    title="Auto-calculated from components"
                                />
                            </div>
                            <div className="form-group form-group--w-60">
                                <label className="form-label-mini form-label-mini--sub">Amount</label>
                                <input
                                    type="number"
                                    className="glass-box-input"
                                    value={subProduct.amount}
                                    onChange={(e) => onUpdateSubProduct(subProduct.id, { amount: parseInt(e.target.value) || 1 })}
                                />
                            </div>
                            <div className="form-group form-group--w-90">
                                <label className="form-label-mini form-label-mini--sub">Currency</label>
                                <select
                                    className="glass-box-select"
                                    value={subProduct.currency}
                                    onChange={(e) => onUpdateSubProduct(subProduct.id, { currency: e.target.value })}
                                    disabled={isLocked}
                                >
                                    <option value="USD">USD</option>
                                    <option value="EUR">EUR</option>
                                    <option value="CNY">CNY</option>
                                </select>
                            </div>
                            <div className="form-group form-group--w-80">
                                <label className="form-label-mini form-label-mini--sub">Unit</label>
                                <select
                                    className="glass-box-select"
                                    value={subProduct.unit}
                                    onChange={(e) => onUpdateSubProduct(subProduct.id, { unit: e.target.value })}
                                >
                                    <option value="Case">Case</option>
                                    <option value="kg">kg</option>
                                    <option value="mt">mt</option>
                                    <option value="unit">unit</option>
                                </select>
                            </div>

                            {/* Actions Column */}
                            <div className="subproduct-actions vertical">
                                <button
                                    className="action-btn-circle"
                                    onClick={() => onToggleSubProductCollapse(subProduct.id)}
                                    type="button"
                                >
                                    {subProduct.collapsed ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
                                </button>

                                <button
                                    className="action-btn-circle action-btn-circle--red"
                                    onClick={() => onRemoveSubProduct(subProduct.id)}
                                >
                                    <Trash2 size={14} />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {!subProduct.collapsed && (
                    <div className="inner-components-table">
                        <div className="inner-table-head">
                            <div className="col-grip"></div>
                            <div className="col-checkbox"></div>
                            <div className="col-name">Name</div>
                            <div className="col-price">Price</div>
                            <div className="col-amount">Amount</div>
                            <div className="col-currency">Currency</div>
                            <div className="col-unit">Unit</div>
                            <div className="col-source">Source</div>
                            <div className="col-destination">
                                <span>Destination</span>
                            </div>
                            <div className="col-actions">
                                {!isLocked && (
                                    <button
                                        className="header-icon-btn btn-icon-pink"
                                        onClick={() => onUngroupSubProduct(subProduct.id)}
                                        title="Ungroup Sub-Product"
                                    >
                                        <Ungroup size={12} />
                                    </button>
                                )}
                            </div>
                        </div>
                        <div className="inner-table-body">
                            {subProduct.components.length > 0 ? (
                                subProduct.components.map(component => renderComponentRow(component, true, subProduct.id))
                            ) : (
                                <div className="empty-subproduct-message">No components in this sub-product</div>
                            )}
                            {/* Empty Dotted Zone for Sub-Product */}
                            <div
                                className="component-row-placeholder dashed"
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                onDrop={(e) => handleDrop(e, subProduct.id)}
                            >
                                <span className="placeholder-text">Drag items here</span>
                            </div>
                        </div>
                    </div>
                )
                }
            </div >
        );
    };

    const hasContent = components.length > 0 || subProducts.length > 0;

    // Drag and Drop Handlers
    const handleDragOver = (e) => {
        e.preventDefault();
        e.currentTarget.classList.add('drag-active');
    };

    const handleDragLeave = (e) => {
        e.currentTarget.classList.remove('drag-active');
    };

    const handleDrop = (e, subProductId = null) => {
        e.preventDefault();
        e.currentTarget.classList.remove('drag-active');

        try {
            const componentData = JSON.parse(e.dataTransfer.getData('application/json'));
            if (onDropItem) {
                onDropItem(componentData, subProductId);
            }
        } catch (err) {
            console.error('Failed to parse dropped item', err);
        }
    };

    if (!hasContent) {
        return (
            <div
                className="model-builder-table-empty"
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e)}
            >
                <p>No components added yet. Click items or drag them here to add.</p>
            </div>
        );
    }

    return (
        <div className="model-builder-wrapper">
            {/* Render sub-products cards first */}
            <div className="sub-products-container">
                {subProducts.map(renderSubProduct)}
            </div>

            {/* Table for stand-alone components */}
            {components.length > 0 && (
                <div className="model-builder-table-glass">
                    <div className="glass-table-head">
                        <div className="col-grip"></div>
                        <div className="col-checkbox"></div>
                        <div className="col-name">Name</div>
                        <div className="col-price">Price</div>
                        <div className="col-amount">Amount</div>
                        <div className="col-currency">Currency</div>
                        <div className="col-unit">Unit</div>
                        <div className="col-source">Source</div>

                        <div className="col-destination">
                            <span>Destination</span>
                        </div>

                        <div className="col-actions">
                            {selectedCount >= 2 && (
                                <button
                                    className="btn-action-pill btn-action-pill--group"
                                    onClick={onGroupSelected}
                                    title={`Group Selected (${selectedCount})`}
                                >
                                    <GitMerge size={16} />
                                </button>
                            )}
                        </div>
                    </div>

                    <div
                        className="glass-table-body"
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e)}
                    >
                        {components.map((component, index) => renderComponentRow(component, false, null, index))}

                        {/* Drag Target Placeholder for Main List */}
                        <div className="component-row-placeholder dashed">
                            <span className="placeholder-text">Drag items here</span>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ModelBuilderTable;



